import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../lib/api';
import GoldButton from '../components/GoldButton';
import WizardStepper from '../components/WizardStepper';
import ColorSelector from '../components/ColorSelector';
import { CheckCircle2, XCircle, ExternalLink, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';

const formatUSD = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value) || 0);

export default function ClienteWizard() {
  const { cedula } = useParams();
  const navigate = useNavigate();
  const [beneficiario, setBeneficiario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [coloresSeleccionados, setColoresSeleccionados] = useState([]);
  const [showRechazadoModal, setShowRechazadoModal] = useState(false);

  useEffect(() => {
    fetchBeneficiario();
  }, [cedula]);

  const fetchBeneficiario = async () => {
    try {
      const response = await api.get(`/cliente/${cedula}`);
      setBeneficiario(response.data);
      
      if (response.data.estado === 'aprobado' || response.data.wizard_completado) {
        navigate(`/cliente/${cedula}/success`);
      } else if (response.data.estado === 'rechazado_por_cliente') {
        setShowRechazadoModal(true);
      }
      
      if (response.data.colores_seleccionados?.length > 0) {
        setColoresSeleccionados(response.data.colores_seleccionados);
      }
    } catch (error) {
      toast.error('Beneficiario no encontrado');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleRechazo = async (motivo) => {
    try {
      await api.put(`/cliente/${cedula}/rechazo`, { motivo });
      setShowRechazadoModal(true);
    } catch (error) {
      toast.error('Error al procesar rechazo');
    }
  };

  const handleNext = async () => {
    // Paso 1: Confirmación de modelo de moto
    if (currentStep === 1) {
      try {
        await api.put(`/cliente/${cedula}/wizard`, {
          acepta_moto: true,
          confirmar_modelo: true
        });
        toast.success('Modelo de moto confirmado');
      } catch (error) {
        toast.error('Error al confirmar modelo');
        return;
      }
    }
    
    // Paso 2: Confirmación de aporte
    if (currentStep === 2) {
      try {
        await api.put(`/cliente/${cedula}/wizard`, {
          acepta_aportes: true,
          confirmar_aporte: true
        });
        toast.success('Aporte confirmado');
      } catch (error) {
        toast.error('Error al confirmar aporte');
        return;
      }
    }
    
    // Paso 3: Confirmación de deuda
    if (currentStep === 3) {
      try {
        await api.put(`/cliente/${cedula}/wizard`, {
          acepta_deuda: true,
          confirmar_deuda: true
        });
        toast.success('Términos de deuda confirmados');
      } catch (error) {
        toast.error('Error al confirmar deuda');
        return;
      }
    }
    
    // Paso 4: Confirmación de colores
    if (currentStep === 4) {
      if (coloresSeleccionados.length < 5) {
        toast.error('Debes seleccionar exactamente 5 colores');
        return;
      }
      
      try {
        await api.put(`/cliente/${cedula}/wizard`, {
          colores_seleccionados: coloresSeleccionados,
          confirmar_colores: true
        });
        toast.success('Colores confirmados');
      } catch (error) {
        toast.error('Error al guardar colores');
        return;
      }
    }
    
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleComplete = async () => {
    const todosDocumentosValidados = beneficiario?.documentos?.every(
      doc => doc.estado === 'validado'
    );

    if (!todosDocumentosValidados) {
      toast.error('Todos los documentos deben estar validados por el administrador');
      return;
    }

    try {
      // Iniciar el período de verificación de 72 horas
      await api.put(`/cliente/${cedula}/wizard`, {
        wizard_completado: true,
        colores_seleccionados: coloresSeleccionados,
        fecha_verificacion_inicio: new Date().toISOString(),
        estado: 'en_verificacion'
      });
      toast.success('¡Felicidades! Tu proceso ha sido completado');
      // Redirigir a página de verificación
      navigate(`/cliente/${cedula}/verification`);
    } catch (error) {
      toast.error('Error al completar wizard');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center">
        <div className="text-[#F5A623] font-heading text-xl">Cargando...</div>
      </div>
    );
  }

  const datosFinancieros = beneficiario?.datos_financieros || {};

  return (
    <div className="min-h-screen bg-[#1A1A1A] py-12 px-6">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_cb23de51-57d5-4133-8f16-28731c09c0ec/artifacts/6u7zs9oj_KOMPA%204.png"
            alt="KOMPA Logo"
            className="w-16 h-16 mx-auto object-contain mb-4"
          />
          <h1 className="text-4xl text-gradient-kompa mb-2 font-bold">
            TU META ESTÁ CERCA
          </h1>
        </div>

        <WizardStepper currentStep={currentStep} />

        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-[#2A2A2A] border-2 border-white/10 rounded-xl p-8 mt-8"
          data-testid={`wizard-step-${currentStep}`}
        >
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="font-heading text-2xl text-[white] font-bold">Confirmación de Moto</h2>
              <div className="space-y-4">
                <div className="p-4 bg-[#2A2A2A] rounded-lg">
                  <p className="text-[#666666] text-sm mb-2">Modelo seleccionado:</p>
                  <p className="text-[white] font-heading text-xl font-semibold">
                    {datosFinancieros.modelo_moto || 'Por definir'}
                  </p>
                </div>
                <div className="flex gap-4">
                  <GoldButton
                    onClick={handleNext}
                    variant="primary"
                    className="flex-1"
                    testId="accept-moto-button"
                  >
                    Aceptar
                  </GoldButton>
                  <GoldButton
                    onClick={() => handleRechazo('Modelo de moto')}
                    variant="secondary"
                    className="flex-1"
                    testId="reject-moto-button"
                  >
                    Rechazar
                  </GoldButton>
                </div>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="font-heading text-2xl text-[white] font-bold">Confirmación de Aportes</h2>
              <div className="space-y-4">
                <div className="p-4 bg-[#2A2A2A] rounded-lg">
                  <p className="text-[#666666] text-sm mb-2">Total aportado:</p>
                  <p className="text-[#10B981] font-heading text-3xl font-bold" data-testid="total-aportado-display">
                    {formatUSD(datosFinancieros.total_aportado ?? datosFinancieros.aporte)}
                  </p>
                </div>
                <div className="flex gap-4">
                  <GoldButton
                    onClick={handleNext}
                    variant="primary"
                    className="flex-1"
                    testId="accept-aportes-button"
                  >
                    Aceptar
                  </GoldButton>
                  <GoldButton
                    onClick={() => handleRechazo('Monto de aportes')}
                    variant="secondary"
                    className="flex-1"
                    testId="reject-aportes-button"
                  >
                    Rechazar
                  </GoldButton>
                </div>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="font-heading text-2xl text-[white] font-bold">Confirmación de Deuda</h2>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-[#2A2A2A] rounded-lg">
                    <p className="text-[#666666] text-sm mb-2">Monto restante:</p>
                    <p className="text-[#F59E0B] font-heading text-2xl font-bold">
                      REF {datosFinancieros.restante?.toFixed(2) || '0.00'}
                    </p>
                  </div>
                  <div className="p-4 bg-[#2A2A2A] rounded-lg">
                    <p className="text-[#666666] text-sm mb-2">Cuotas mensuales:</p>
                    <p className="text-[#F5A623] font-heading text-2xl font-bold">
                      {datosFinancieros.tiempo_restante?.toFixed(0) || '0'}
                    </p>
                  </div>
                </div>
                <div className="p-4 bg-[#2A2A2A] rounded-lg">
                  <p className="text-[#666666] text-sm mb-2">Valor de cuota mensual:</p>
                  <p className="text-[white] font-heading text-xl font-semibold">
                    REF {datosFinancieros.valor_cuota?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div className="flex gap-4">
                  <GoldButton
                    onClick={handleNext}
                    variant="primary"
                    className="flex-1"
                    testId="accept-deuda-button"
                  >
                    Estoy de Acuerdo
                  </GoldButton>
                  <GoldButton
                    onClick={() => handleRechazo('Términos de deuda')}
                    variant="secondary"
                    className="flex-1"
                    testId="reject-deuda-button"
                  >
                    No estoy de acuerdo
                  </GoldButton>
                </div>
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-6">
              <h2 className="font-heading text-2xl text-[white] font-bold">Selección de Color</h2>
              <ColorSelector
                selected={coloresSeleccionados}
                onChange={setColoresSeleccionados}
                minSelection={3}
                maxSelection={5}
              />
              <GoldButton
                onClick={handleNext}
                variant="primary"
                className="w-full"
                disabled={coloresSeleccionados.length < 3 || coloresSeleccionados.length > 5}
                testId="continue-to-docs-button"
              >
                Continuar a Documentos
              </GoldButton>
            </div>
          )}

          {currentStep === 5 && (
            <div className="space-y-6">
              <h2 className="font-heading text-2xl text-[white] font-bold">Checklist de Documentos</h2>
              <div className="space-y-3">
                {beneficiario?.documentos?.map((doc, index) => {
                  const isValidado = doc.estado === 'validado';
                  const isRechazado = doc.estado === 'rechazado';
                  const isPendiente = doc.estado === 'pendiente' || doc.estado === 'cargado';
                  
                  return (
                    <div
                      key={index}
                      className={`flex items-center justify-between p-4 rounded-lg border-2 ${
                        isValidado 
                          ? 'bg-[#10B981]/10 border-[#10B981]/30' 
                          : isRechazado 
                          ? 'bg-[#EF4444]/10 border-[#EF4444]/30' 
                          : 'bg-[#F59E0B]/10 border-[#F59E0B]/30'
                      }`}
                      data-testid={`document-item-${index}`}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        {isValidado ? (
                          <CheckCircle2 className="w-6 h-6 text-[#10B981]" />
                        ) : isRechazado ? (
                          <XCircle className="w-6 h-6 text-[#EF4444]" />
                        ) : (
                          <AlertCircle className="w-6 h-6 text-[#F59E0B]" />
                        )}
                        <div>
                          <p className="text-[white] font-body font-medium">{doc.nombre}</p>
                          <p className={`text-sm mt-1 font-semibold ${
                            isValidado ? 'text-[#10B981]' : isRechazado ? 'text-[#EF4444]' : 'text-[#F59E0B]'
                          }`}>
                            {isValidado ? '✅ Validado' : isRechazado ? '❌ Rechazado - Volver a cargar' : '⏳ Pendiente de carga'}
                          </p>
                        </div>
                      </div>
                      {!isValidado && (
                        <a
                          href="https://www.kompa.online"
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                            isRechazado
                              ? 'bg-[#EF4444] hover:bg-[#DC2626] text-white'
                              : 'bg-[#F5A623] hover:bg-[#FF9500] text-black'
                          }`}
                        >
                          {isRechazado ? 'Volver a Cargar' : 'Cargar Documento'}
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
              <GoldButton
                onClick={handleComplete}
                variant="primary"
                className={`w-full text-lg ${!beneficiario?.documentos?.every(doc => doc.estado === 'validado') ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={!beneficiario?.documentos?.every(doc => doc.estado === 'validado')}
                testId="complete-wizard-button"
              >
                OBTENER MI META 🚀
              </GoldButton>
              {!beneficiario?.documentos?.every(doc => doc.estado === 'validado') && (
                <p className="text-[#F59E0B] text-sm text-center mt-2 font-semibold">
                  ⏳ El botón estará disponible cuando el administrador valide todos tus documentos
                </p>
              )}
            </div>
          )}
        </motion.div>

        {currentStep > 1 && currentStep < 5 && (
          <button
            onClick={() => setCurrentStep(currentStep - 1)}
            className="mt-4 text-[#666666] hover:text-[#F5A623] font-body transition-colors"
            data-testid="back-button"
          >
            ← Volver
          </button>
        )}
      </div>

      <Dialog open={showRechazadoModal} onOpenChange={() => {}}>
        <DialogContent className="bg-gradient-to-br from-[#2A2A2A] to-[#2A2A2A] border border-[#EF4444]/30 rounded-2xl p-8 max-w-md" data-testid="rejection-modal">
          <DialogTitle className="text-2xl font-heading font-bold text-[white] mb-4">
            Beneficio Rechazado
          </DialogTitle>
          <DialogDescription className="text-[#666666] font-body leading-relaxed mb-6">
            USTED HA RECHAZADO SU PROPIO BENEFICIO. Comuníquese al WhatsApp <a href="https://wa.me/584127881253" className="text-[#F5A623] hover:text-[#FF9500]">0412-7881253</a> para soporte.
          </DialogDescription>
          <GoldButton
            onClick={() => navigate('/')}
            variant="primary"
            className="w-full"
            testId="return-home-button"
          >
            Volver al Inicio
          </GoldButton>
        </DialogContent>
      </Dialog>
    </div>
  );
}